import React, { useContext, useEffect, useReducer, useState } from "react";
import MaterialTable from "material-table";
import {
    Typography,
    IconButton,
    Grid,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import HistoryIcon from "@material-ui/icons/History";
import tableIcons, { options } from "components/universal/table_attributes";
import { ToastMessageContext } from "lib/contexts/message_context";
import TableAction from "actions/table_actions";
import tableReducer, { INITIAL_TABLE_STATE } from "reducers/table_reducer";
import { BASE_API_URL_BOXR } from "lib/constants";
import { formatDate } from "lib/utils/common_utils";
import MROButton from "components/buttons";
import { ICON_COLOUR } from "lib/constants/style_constants";
import HistoryTablePopup from "views/Compliance/UserCompliance/UserDetails/HistoryPopup";
import History from "views/Compliance/UserCompliance/UserDetails/History"


function Authorization({ currentSelection, id }) {
    const message = useContext(ToastMessageContext);
    const [tableState, dispatch] = useReducer(tableReducer, INITIAL_TABLE_STATE);
    const { data, inProgress } = tableState;

    const [selectedApproval, setSelectedApproval] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showApprovals, setShowApproval] = useState(false)
    // Fetch approvals when selection changes
    useEffect(() => {
        if (id) {
            const params = { user_id: id };
            TableAction.getList(dispatch, message, params, "userApprovals");
        }
    }, [id]);

    // Status styles
    const statusMapButtons = {
        "On Track": "button-positive",
        "Near Due": "button-warning",
        "Expired": "button-negative",
        "Invalid": "button-negative",
    };

    const statusMap = {
        "Good": "text-positive",
        "Near Due": "text-warning",
        "Overdue": "text-negative",
    };

    // Open document or show toast if missing
    const viewDoc = (doc) => {
        if (!doc) {
            message.showToastMessage({
                open: true,
                message: "No document found",
                variant: "error",
            });
            return;
        }
        window.open(`${BASE_API_URL_BOXR}${doc}`, "_blank");
    };

    // Columns for MaterialTable
    const columns = [
        {
            title: "Name",
            field: "name",
            headerStyle: { textAlign: "left", paddingLeft: "4%" },
            cellStyle: { textAlign: "left", paddingLeft: "4%" },
        },
    ];

    const tableOptions = {
        ...options,
        search: false,
        paging: true,
        pageSize: 5,
        actionsColumnIndex: -1,
        toolbar: false,
        headerStyle: { paddingRight: "50px" },
        rowStyle: { height: "42px" },
    };

    // Render
    return (
        <div>
            <MaterialTable
                style={{
                    boxShadow: "0px 1px 3px #00000033",
                }}
                icons={tableIcons}
                title={
                    <Typography variant="subtitle2" color="secondary">
                        Authorisations
                    </Typography>
                }
                columns={columns}
                data={data?.data || []}
                isLoading={inProgress}
                options={tableOptions}
                localization={{
                    header: { actions: "Permissions" },
                    body: { emptyDataSourceMessage: "No Approvals Found" },
                }}
                actions={[
                    {
                        icon: () => (
                            <VisibilityIcon fontSize="small" htmlColor={ICON_COLOUR} />
                        ),
                        tooltip: "View Approval",
                        onClick: (event, rowData) => {

                            setShowHistory(true);
                            setSelectedApproval(rowData)
                        },
                    },
                ]}
            />

            {/* Details Section (bottom info bar) */}
            {data?.status && (
                <div
                    style={{
                        backgroundColor: "#fff",
                        // padding: "10px",
                        marginTop: "10px",
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        justify="space-between"
                        style={{
                            backgroundColor: "#f3f3f3",
                            padding: "10px 25px",
                        }}
                    >
                        {/* Expiry & Document */}
                        <Grid item md={2}>
                            <Typography variant="subtitle2">Expiry</Typography>
                            <Typography variant="subtitle2">Document</Typography>
                        </Grid>

                        <Grid item md={4}>
                            <Typography
                                variant="subtitle2"
                                className={
                                    data.digital_doc_signed
                                        ? statusMapButtons[data.status]
                                        : ""
                                }
                                style={{
                                    color: !data.digital_doc_signed ? "#FF0202" : undefined,
                                    marginTop: "-8px",
                                }}
                            >
                                {data.digital_doc_signed
                                    ? formatDate(data.expiry)
                                    : "Needs Signature"}
                            </Typography>

                            {data.auth_doc ? (
                                <Typography
                                    align="center"
                                    component="a"
                                    href={`${BASE_API_URL_BOXR}${data.auth_doc}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: "#6b6767",
                                        fontWeight: "bold",
                                        textDecoration: "none",
                                    }}
                                >
                                    <VisibilityIcon htmlColor={ICON_COLOUR} />
                                </Typography>
                            ) : (
                                <Typography variant="subtitle2" align="center">
                                    Document Not Found
                                </Typography>
                            )}
                        </Grid>

                        {/* Status & Issue */}
                        <Grid item md={2}>
                            <Typography variant="subtitle2">Status</Typography>
                            <Typography variant="subtitle2">
                                <b>Issue #</b>
                            </Typography>
                        </Grid>

                        <Grid item md={3}>
                            <MROButton
                                size="small"
                                variant="outlined"
                                className={statusMapButtons[data.status]}
                            >
                                {data.status}
                            </MROButton>
                            <Typography
                                align="center"
                                variant="subtitle1"
                                style={{
                                    marginRight: data?.revision_number ? "75px" : "120px",
                                }}
                            >
                                {data?.revision_number || "-"}
                            </Typography>
                        </Grid>

                        {/* History Icon */}
                        <Grid item md={1}>
                            <IconButton
                                onClick={() => {
                                    setSelectedApproval(currentSelection);
                                    setShowApproval(true)
                                }}
                            >
                                <HistoryIcon />
                            </IconButton>

                            {showApprovals && (
                                <HistoryTablePopup
                                    busy={busy}
                                    open={showApprovals}
                                    handleClose={() => setShowApproval(null)}
                                    currentData={{ id: id }}
                                    name="authorisation_list_id"
                                />
                            )}
                        </Grid>
                    </Grid>
                </div>
            )}

            {/* Approval Scopes Popup */}
            {showHistory && (
                <History
                    handleClose={() => setShowHistory(false)}

                    currentSelection={selectedApproval}
                />
            )}
        </div>
    );
}

export default Authorization;
